/**
 * Cart store — persists to localStorage. Each line is a snapshot of the
 * menu item at add-time (name, base price, selected customizations) so
 * the cart remains accurate even if the menu changes before checkout.
 *
 * Line totals are computed on demand from the snapshot, never persisted,
 * to keep the source of truth in one place.
 */
import { computeLineTotal, LS_KEYS, type SelectedCustomization } from '@cracked-mug/shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface CartLine {
  cartItemId: string;
  itemId: string;
  itemName: string;
  imageUrl: string | null;
  basePriceCents: number;
  quantity: number;
  selectedCustomizations: SelectedCustomization[];
  specialInstructions: string;
}

interface CartState {
  items: CartLine[];
  addItem: (line: Omit<CartLine, 'cartItemId'>) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  updateInstructions: (cartItemId: string, instructions: string) => void;
  clear: () => void;
}

let cartIdSeq = 0;
function generateCartItemId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Non-secure-context fallback (e.g. a plain-HTTP LAN test build, where
  // crypto.randomUUID is unavailable). A monotonic counter guarantees
  // uniqueness even for two adds within the same millisecond.
  cartIdSeq += 1;
  return `${String(Date.now())}-${String(cartIdSeq)}-${Math.random().toString(36).slice(2)}`;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      addItem: (line) =>
        set((state) => ({
          items: [...state.items, { ...line, cartItemId: generateCartItemId() }],
        })),
      removeItem: (cartItemId) =>
        set((state) => ({
          items: state.items.filter((i) => i.cartItemId !== cartItemId),
        })),
      updateQuantity: (cartItemId, quantity) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.cartItemId === cartItemId ? { ...i, quantity: Math.max(1, quantity) } : i,
          ),
        })),
      updateInstructions: (cartItemId, instructions) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.cartItemId === cartItemId ? { ...i, specialInstructions: instructions } : i,
          ),
        })),
      clear: () => set({ items: [] }),
    }),
    {
      name: LS_KEYS.cart,
      version: 1,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/** Total quantity across all lines — used for the floating cart badge. */
export function selectCartItemCount(state: CartState): number {
  return state.items.reduce((sum, i) => sum + i.quantity, 0);
}

/** Sum of all line totals in cents. */
export function selectCartSubtotalCents(state: CartState): number {
  return state.items.reduce(
    (sum, i) => sum + computeLineTotal(i.basePriceCents, i.selectedCustomizations, i.quantity),
    0,
  );
}
